import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';

import type { AiListingAutofillResponse } from '../../types/aiListingAutofill';

import {
  createAiReviewAttention,
  getAiReviewMessages,
  getAiSectionReviewItems,
  markAiReviewItemReviewed,
  type AiReviewAttention,
  type AiReviewTarget,
} from './aiListingReviewMapping';

type LocationStatus = AiListingAutofillResponse['location']['status'];

interface AiReviewContextValue {
  attention: readonly AiReviewAttention[];
  locationStatus: LocationStatus | null;
  resolveField: (field: AiReviewTarget) => void;
  resolveLocation: () => void;
  markReviewed: (field: string, target: AiReviewTarget) => void;
}

const AiReviewContext = createContext<AiReviewContextValue | null>(null);

export const useAiListingReview = () => useContext(AiReviewContext);

export const AiListingReviewProvider = ({
  children,
  locationStatus,
  review,
}: {
  children: ReactNode;
  locationStatus?: LocationStatus;
  review?: AiListingAutofillResponse['review'];
}) => {
  const [attention, setAttention] = useState(() => createAiReviewAttention(review));
  const [unresolvedLocationStatus, setUnresolvedLocationStatus] = useState<LocationStatus | null>(
    locationStatus === 'not_found' || locationStatus === 'ambiguous' ? locationStatus : null
  );
  const resolveField = useCallback((field: AiReviewTarget) => {
    setAttention((current) =>
      current.some((item) => item.target === field)
        ? current.filter((item) => item.target !== field)
        : current
    );
  }, []);
  const resolveLocation = useCallback(() => {
    setUnresolvedLocationStatus(null);
    setAttention((current) =>
      current.some((item) => item.target === 'location')
        ? current.filter((item) => item.target !== 'location')
        : current
    );
  }, []);
  const markReviewed = useCallback((field: string, target: AiReviewTarget) => {
    setAttention((current) => markAiReviewItemReviewed(current, field, target));
  }, []);

  return (
    <AiReviewContext.Provider
      value={{
        attention,
        locationStatus: unresolvedLocationStatus,
        resolveField,
        resolveLocation,
        markReviewed,
      }}
    >
      {children}
    </AiReviewContext.Provider>
  );
};

export const AiReviewBanner = () => {
  const review = useAiListingReview();
  if (!review || (review.attention.length === 0 && !review.locationStatus)) return null;

  return (
    <Alert severity='warning'>
      <AlertTitle>Some listing details need your attention.</AlertTitle>
      Please review the highlighted fields before publishing.
    </Alert>
  );
};

const SECTION_REVIEW_LABELS: Record<string, string> = {
  plotArea: 'Plot area',
  toDeveloper: 'To Developer',
  prices: 'Pricing details',
  amenities: 'Amenities',
  commission: 'Commission details',
  commissions: 'Commission details',
  title: 'Listing title',
};

const getSectionReviewLabel = (field: string, index: number, count: number): string => {
  const name = field.split('.').pop() ?? field;
  if (Object.prototype.hasOwnProperty.call(SECTION_REVIEW_LABELS, name)) {
    return SECTION_REVIEW_LABELS[name];
  }
  return count > 1 ? `Additional listing detail ${index + 1}` : 'Additional listing detail';
};

export const AiReviewFieldNotice = ({ target }: { target: AiReviewTarget }) => {
  const review = useAiListingReview();
  if (target.startsWith('section:')) {
    const items = review ? getAiSectionReviewItems(review.attention, target) : [];
    if (items.length === 0) return null;

    return (
      <Stack marginTop={0.75} spacing={1.5}>
        {items.map(({ field, messages }, index) => (
          <Stack
            alignItems='flex-start'
            color='warning.dark'
            direction='row'
            key={field}
            spacing={0.75}
          >
            <WarningAmberOutlinedIcon aria-hidden='true' fontSize='small' />
            <Stack alignItems='flex-start' spacing={0.25}>
              <Typography component='p' fontWeight={600} variant='body2'>
                {getSectionReviewLabel(field, index, items.length)}
              </Typography>
              {messages.map((message) => (
                <Typography component='p' key={message} variant='body2'>
                  {message}
                </Typography>
              ))}
              <Button
                color='inherit'
                onClick={() => review?.markReviewed(field, target)}
                size='small'
                sx={{ alignSelf: 'flex-start', minWidth: 0, padding: 0, textTransform: 'none' }}
                type='button'
                variant='text'
              >
                Mark as reviewed
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  }

  const messages = review ? getAiReviewMessages(review.attention, target) : [];
  if (messages.length === 0) return null;

  return (
    <Stack
      alignItems='flex-start'
      color='warning.dark'
      direction='row'
      marginTop={0.75}
      spacing={0.75}
    >
      <WarningAmberOutlinedIcon aria-hidden='true' fontSize='small' />
      <Stack spacing={0.25}>
        {messages.map((message) => (
          <Typography component='p' key={message} variant='body2'>
            {message}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
};

export const AiReviewLocationNotice = () => {
  const review = useAiListingReview();
  const message =
    review?.locationStatus === 'not_found'
      ? "We couldn't match the location automatically. Please select it manually."
      : review?.locationStatus === 'ambiguous'
        ? 'We found multiple possible locations. Please select the correct one.'
        : null;
  if (!message) return null;

  return <Alert severity='warning'>{message}</Alert>;
};
